import { createFileRoute, useParams, Link } from '@tanstack/react-router'
import { useState, useEffect, useMemo } from 'react'
import { PageContentWrapper, SimpleButton, SimpleCard } from '@unwrittenmedia/simple-components'
import SimplePageHeader2 from '@/components/SimplePageHeader2'
import { useGQL_teams } from '@/contexts/graphql-and-swr-apis'
import { getEncoderConfig, postEncoderConfig, type EncoderConfig } from '@/contexts/graphql-functions/encoderConfig'
import { faCheck, faServer } from '@fortawesome/pro-regular-svg-icons'
import { faTv, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { clsx } from 'clsx'

export const Route = createFileRoute('/encoder/$encoderId')({
    component: EncoderPage,
})

function EncoderPage() {
    const { encoderId } = useParams({ from: '/encoder/$encoderId' })

    const [config, setConfig] = useState<EncoderConfig | null>(null)
    const [configLoading, setConfigLoading] = useState(true)
    const [configError, setConfigError] = useState<string | null>(null)

    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)

    const teamInfos = useGQL_teams()

    const sortedTeams = useMemo(() => {
        const teams = teamInfos.data?.teams || []
        return [...teams].filter((t) => !t.deleted).sort((a, b) => a.name.localeCompare(b.name))
    }, [teamInfos.data])

    const flatScreens = useMemo(() => {
        return sortedTeams.flatMap((team) =>
            (team.places || []).map((place) => ({ team, place }))
        )
    }, [sortedTeams])

    // Load current encoder config on mount
    useEffect(() => {
        let cancelled = false
        setConfigLoading(true)
        setConfigError(null)

        getEncoderConfig(encoderId)
            .then((res) => {
                if (!cancelled) {
                    setConfig(res)
                    setConfigLoading(false)
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setConfigError(err instanceof Error ? err.message : 'Failed to load encoder config')
                    setConfigLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [encoderId])

    async function handleSelectScreen(teamId: string, placeId: string) {
        setSaving(true)
        setSaveError(null)
        try {
            const res = await postEncoderConfig(encoderId, teamId, placeId)
            setConfig(res)
        } catch (err: unknown) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save encoder config')
        } finally {
            setSaving(false)
        }
    }

    return (
        <PageContentWrapper>
            <SimplePageHeader2
                title="Encoder Setup"
                description={`Link this encoder to your congregation.`}
            />

            {/* Encoder ID display */}
            <div className="mt-6 mb-8 p-4 bg-gray-100 dark:bg-mist-900 rounded-lg flex items-center gap-3">
                <FontAwesomeIcon icon={faServer} className="text-gray-500 dark:text-mist-500" />
                <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-mist-500 uppercase tracking-wide">
                        Encoder ID
                    </p>
                    <p className="font-mono text-sm text-gray-900 dark:text-white">{encoderId}</p>
                </div>
            </div>

            {/* Current config */}
            {configLoading ? (
                <p className="text-sm text-gray-500 dark:text-mist-500">Loading encoder config…</p>
            ) : configError ? (
                <div className="mb-6 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                    <p className="text-sm text-red-700 dark:text-red-400">{configError}</p>
                </div>
            ) : config ? (
                <div className="mb-8 p-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                    <div className="flex items-center gap-2 mb-2">
                        <FontAwesomeIcon icon={faCheck} className="text-green-600 dark:text-green-400" />
                        <span className="font-semibold text-green-800 dark:text-green-300 text-sm">
                            Screen linked
                        </span>
                        <Link
                            to="/team/$teamId/place/$placeId"
                            params={{ teamId: config.teamId, placeId: config.placeId }}
                            className="ml-auto text-xs font-medium p-3 rounded-md bg-green-600 hover:bg-green-700 text-white dark:bg-green-700 dark:hover:bg-green-600"
                        >
                            View Screen
                        </Link>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <dt className="text-gray-500 dark:text-mist-500">Team</dt>
                        <dd className="text-gray-900 dark:text-white font-medium">{config.teamName}</dd>
                        <dt className="text-gray-500 dark:text-mist-500">Screen</dt>
                        <dd className="text-gray-900 dark:text-white font-medium">{config.placeName}</dd>
                        <dt className="text-gray-500 dark:text-mist-500">Screen ID</dt>
                        <dd className="font-mono text-gray-700 dark:text-mist-400 text-xs">{config.placeId}</dd>
                        <dt className="text-gray-500 dark:text-mist-500">Encoder Key</dt>
                        <dd className="font-mono text-gray-700 dark:text-mist-400 text-xs">{config.encoderKey}</dd>
                    </dl>
                    <p className="mt-3 text-xs text-gray-500 dark:text-mist-500">
                        You can reassign this encoder to a different screen below.
                    </p>
                </div>
            ) : (
                <div className="mb-6 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                        This encoder has not been linked to a screen yet. Choose one below.
                    </p>
                </div>
            )}

            {/* Screen picker */}
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                {config ? 'Reassign screen' : 'Select a screen'}
            </h2>

            {saveError && (
                <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                    <p className="text-sm text-red-700 dark:text-red-400">{saveError}</p>
                </div>
            )}

            {teamInfos.isLoading ? (
                <p className="text-sm text-gray-500 dark:text-mist-500">Loading teams…</p>
            ) : flatScreens.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-mist-500">No screens found.</p>
            ) : (
                <div className="flex flex-col gap-2">
                    {flatScreens.map(({ team, place }) => {
                        const isSelected = config?.placeId === place.id
                        return (
                            <button
                                key={place.id}
                                disabled={saving}
                                onClick={() => handleSelectScreen(team.id, place.id)}
                                className={clsx(
                                    'flex items-center gap-4 w-full px-4 py-3 rounded-lg border-2 transition-colors cursor-pointer text-left',
                                    'focus:outline-none focus:ring-2 focus:ring-blue-500',
                                    isSelected
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                        : 'border-gray-200 dark:border-mist-800 hover:border-blue-300 dark:hover:border-blue-600 bg-white dark:bg-mist-900',
                                    saving && 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                <FontAwesomeIcon
                                    icon={faTv}
                                    className={clsx(
                                        'text-2xl shrink-0',
                                        isSelected
                                            ? 'text-blue-500 dark:text-blue-400'
                                            : 'text-gray-400 dark:text-mist-600'
                                    )}
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-gray-900 dark:text-white text-sm truncate">
                                        {place.name}
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-mist-500 truncate">
                                        {team.name}
                                    </p>
                                </div>
                                {isSelected && (
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className="text-blue-600 dark:text-blue-400 shrink-0"
                                    />
                                )}
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Add new screen */}
            <button
                className={clsx(
                    'mt-4 flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg border-2 border-dashed transition-colors cursor-pointer',
                    'border-gray-300 dark:border-mist-700 text-gray-500 dark:text-mist-500',
                    'hover:border-blue-400 hover:text-blue-500 dark:hover:border-blue-500 dark:hover:text-blue-400',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500'
                )}
            >
                <FontAwesomeIcon icon={faPlus} />
                <span className="text-sm font-medium">Add new screen</span>
            </button>
        </PageContentWrapper>
    )
}
